import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Trash2 } from 'lucide-react';

const PositionsTable = ({ positions, onSort, sortConfig, onDelete }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (positionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(positionId)) {
      newExpanded.delete(positionId);
    } else {
      newExpanded.add(positionId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to check if position is at risk (over 1 month old)
  const isAtRisk = (createdAt) => {
    if (!createdAt) return false;
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    return new Date(createdAt) < oneMonthAgo;
  };

  // Helper function to check if position is reported
  const isReported = (position) => {
    return position.reportedBy && position.reportedBy.length > 0;
  };

  // Helper function to get link status badge (only show if there's an error)
  const getLinkStatusBadge = (position) => {
    if (!position.link) return null;
    
    switch (position.linkStatus) {
      case 'invalid':
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
            Link Error
          </span>
        );
      case 'error':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
            Link Error
          </span>
        );
      default:
        return null; // Don't show anything for valid or not checked
    }
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="text-gray-400">↕</span>;
    }
    return sortConfig.direction === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />;
  };

  const SortableHeader = ({ columnKey, label, currentKey }) => (
    <th
      className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => onSort(columnKey)}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {getSortIcon(columnKey)}
      </div>
    </th>
  );

  return (
    <div className="overflow-x-auto">
      <table className="divide-y divide-gray-200" style={{ width: '100%', tableLayout: 'auto' }}>
        <thead className="bg-gray-100">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none" style={{ maxWidth: '200px' }}>
              <div className="flex items-center space-x-1" onClick={() => onSort('title')}>
                <span>Title</span>
                {getSortIcon('title')}
              </div>
            </th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none" style={{ maxWidth: '200px' }}>
              <div className="flex items-center space-x-1" onClick={() => onSort('institution')}>
                <span>Institution</span>
                {getSortIcon('institution')}
              </div>
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center space-x-1" onClick={() => onSort('country')}>
                <span>Country</span>
                {getSortIcon('country')}
              </div>
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center space-x-1" onClick={() => onSort('prestige_score')}>
                <span>Prestige</span>
                {getSortIcon('prestige_score')}
              </div>
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none">
              <div className="flex items-center space-x-1" onClick={() => onSort('createdAt')}>
                <span>Date Created</span>
                {getSortIcon('createdAt')}
              </div>
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Status
            </th>
            <th className="px-2 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {positions.length > 0 ? (
            positions.map((position) => (
              <React.Fragment key={position._id}>
                <tr
                  className="hover:bg-gray-50 cursor-pointer transition"
                  onClick={() => toggleRow(position._id)}
                >
                  <td className="px-3 py-4 text-sm text-gray-900" style={{ maxWidth: '200px' }}>
                    <div className="flex items-start flex-col">
                      <div className="flex items-center w-full">
                        <span className="font-medium truncate">
                          {position.title || 'N/A'}
                        </span>
                        {expandedRows.has(position._id) ? (
                          <ChevronUp className="ml-1 text-gray-400 flex-shrink-0" size={14} />
                        ) : (
                          <ChevronDown className="ml-1 text-gray-400 flex-shrink-0" size={14} />
                        )}
                      </div>
                      {position.createdBy === "student-submission" && position.createdByUser && (
                        <span className="text-xs text-blue-600 mt-1 truncate w-full">
                          Submitted by: {position.createdByUser.name || 'Unknown'}
                        </span>
                      )}
                      {position.createdBy === "professor" && position.createdByUser && (
                        <span className="text-xs text-green-600 mt-1 truncate w-full">
                          Created by: {position.createdByUser.name || 'Unknown'}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-600" style={{ maxWidth: '150px' }}>
                    <span className="truncate block">{position.institution || 'N/A'}</span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-600">
                    {position.country || 'N/A'}
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-center">
                    <span className={`font-medium ${
                      position.prestige_score >= 0.7 ? 'text-green-600' :
                      position.prestige_score >= 0.4 ? 'text-yellow-600' :
                      'text-gray-600'
                    }`}>
                      {position.prestige_score ? position.prestige_score.toFixed(2) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm text-gray-600">
                    {formatDate(position.createdAt)}
                  </td>
                  <td className="px-2 py-4 text-sm">
                    <div className="flex flex-col gap-1">
                      {position.createdBy === "student-submission" && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                          Student Submission
                        </span>
                      )}
                      {position.createdBy === "professor" && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                          Professor Created
                        </span>
                      )}
                      {isAtRisk(position.createdAt) && (
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs font-medium rounded-full">
                          At Risk
                        </span>
                      )}
                      {isReported(position) && (
                        <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded-full">
                          Reported ({position.reportedBy.length})
                        </span>
                      )}
                      {getLinkStatusBadge(position)}
                    </div>
                  </td>
                  <td className="px-2 py-4 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      {position.link && (
                        <a
                          href={position.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center text-blue-600 hover:text-blue-800"
                        >
                          <ExternalLink size={16} className="mr-1" />
                          Link
                        </a>
                      )}
                      {onDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Are you sure you want to delete "${position.title}"? This action cannot be undone.`)) {
                              onDelete(position._id);
                            }
                          }}
                          className="flex items-center text-red-600 hover:text-red-800 transition-colors"
                          title="Delete position"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>

                  {/* Expanded Details */}
                {expandedRows.has(position._id) && (
                  <tr>
                    <td colSpan="6" className="px-6 py-6 bg-gray-50 border-t border-gray-200">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Description */}
                        <div className="md:col-span-2">
                          <h4 className="font-semibold text-gray-800 mb-2">Description</h4>
                          <p className="text-sm text-gray-700">
                            {position.description || 'No description available'}
                          </p>
                        </div>

                        {/* Basic Info */}
                        <div className="bg-white rounded-lg border p-4">
                          <h4 className="font-semibold text-gray-800 mb-3">Position Details</h4>
                          <div className="space-y-2 text-sm text-gray-700">
                            <div>
                              <span className="font-medium">City:</span>{' '}
                              <span className="text-gray-600">{position.city || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium">Country:</span>{' '}
                              <span className="text-gray-600">{position.country || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium">Start Semester:</span>{' '}
                              <span className="text-gray-600">{position.start_semester || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium">Start Year:</span>{' '}
                              <span className="text-gray-600">{position.start_year || 'N/A'}</span>
                            </div>
                            <div>
                              <span className="font-medium">Native:</span>{' '}
                              <span className="text-gray-600">{position.native ? 'Yes' : 'No'}</span>
                            </div>
                            <div>
                              <span className="font-medium">Created By:</span>{' '}
                              <span className="text-gray-600">{position.createdBy || 'N/A'}</span>
                            </div>
                            {position.createdBy === "student-submission" && position.createdByUser && (
                              <div>
                                <span className="font-medium">Submitted By Student:</span>{' '}
                                <span className="text-gray-600">
                                  {position.createdByUser.name || 'Unknown'} 
                                  {position.createdByUser.email && ` (${position.createdByUser.email})`}
                                </span>
                              </div>
                            )}
                            {position.createdBy === "professor" && position.createdByUser && (
                              <div>
                                <span className="font-medium">Created By Professor:</span>{' '}
                                <span className="text-gray-600">
                                  {position.createdByUser.name || 'Unknown'} 
                                  {position.createdByUser.email && ` (${position.createdByUser.email})`}
                                </span>
                              </div>
                            )}
                            <div>
                              <span className="font-medium">Tracking Count:</span>{' '}
                              <span className="text-gray-600">{position.trackingCount || 0}</span>
                            </div>
                          </div>
                        </div>

                        {/* Fields */}
                        <div className="bg-white rounded-lg border p-4">
                          <h4 className="font-semibold text-gray-800 mb-3">Fields</h4>
                          <div className="flex flex-wrap gap-2">
                            {position.fields && position.fields.length > 0 ? (
                              position.fields.map((field, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {field}
                                </span>
                              ))
                            ) : (
                              <span className="text-sm text-gray-400">No fields specified</span>
                            )}
                          </div>
                        </div>

                        {/* Link */}
                        {position.link && (
                          <div className="md:col-span-2">
                            <a
                              href={position.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              <ExternalLink size={16} className="mr-1" />
                              View Original Position Listing
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))
          ) : (
            <tr>
              <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                No positions found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default PositionsTable;
